import { ReactNode } from 'react';

interface Column<T> {
  key: string;
  header: string;
  width?: string;
  minWidth?: string;
  render?: (item: T) => ReactNode;
  sortable?: boolean;
  /** モバイルカード表示で非表示にする場合はtrue */
  hideOnMobile?: boolean;
  /** モバイルカード表示でメイン（タイトル）として表示する場合はtrue */
  mobileTitle?: boolean;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  sortConfig?: SortConfig;
  onSort?: (key: string) => void;
  /** モバイルでカード表示を使用する（デフォルト: true） */
  mobileCardView?: boolean;
}

export default function Table<T extends { id: string }>({
  columns,
  data,
  loading = false,
  emptyMessage = 'データがありません',
  onRowClick,
  sortConfig,
  onSort,
  mobileCardView = true,
}: TableProps<T>) {
  // dataがnull/undefined/配列でない場合は空配列として扱う
  const safeData = Array.isArray(data) ? data : [];
  const safeColumns = Array.isArray(columns) ? columns : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (safeData.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  const handleHeaderClick = (column: Column<T>) => {
    if (column.sortable && onSort) {
      onSort(column.key);
    }
  };

  const getSortIcon = (column: Column<T>) => {
    if (!column.sortable) return null;
    if (sortConfig?.key !== column.key) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // タイトルカラムを取得
  const titleColumn = safeColumns.find(col => col.mobileTitle) || safeColumns[0];
  // アクションカラム（keyが'actions'のもの）
  const actionColumn = safeColumns.find(col => col.key === 'actions');
  // その他の表示カラム（タイトル・アクション以外でモバイル非表示でないもの）
  const detailColumns = safeColumns.filter(
    col => col !== titleColumn && col !== actionColumn && !col.hideOnMobile
  );

  // モバイルカード表示
  const renderMobileCards = () => (
    <div className="space-y-3 md:hidden">
      {safeData.map((item, index) => (
        <div
          key={item?.id || `card-${index}`}
          className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${
            onRowClick ? 'cursor-pointer active:bg-gray-50 dark:active:bg-gray-700' : ''
          }`}
          onClick={() => onRowClick?.(item)}
        >
          {/* タイトル部分 */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              {titleColumn?.render && item
                ? titleColumn.render(item)
                : (
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {(item as Record<string, unknown>)?.[titleColumn?.key] as ReactNode ?? '-'}
                  </p>
                )}
            </div>
            {/* アクションボタン */}
            {actionColumn?.render && item && (
              <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {actionColumn.render(item)}
              </div>
            )}
          </div>
          
          {/* 詳細情報 */}
          <div className="space-y-2 text-sm">
            {detailColumns.slice(0, 4).map((column, colIndex) => {
              const value = column?.render && item
                ? column.render(item)
                : (item as Record<string, unknown>)?.[column?.key] as ReactNode ?? '-';
              
              return (
                <div key={column?.key || `detail-${colIndex}`} className="flex items-start gap-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs w-20 flex-shrink-0 leading-5">
                    {column.header}
                  </span>
                  <span className="text-gray-900 dark:text-gray-100 break-words leading-5">
                    {value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  // デスクトップテーブル表示
  const renderDesktopTable = () => (
    <div className={`overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 ${mobileCardView ? 'hidden md:block' : ''}`}>
      <div className="inline-block min-w-full align-middle">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              {safeColumns.map((column) => (
                <th
                  key={column.key}
                  className={`px-3 lg:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap ${
                    column.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none' : ''
                  }`}
                  style={{ width: column.width, minWidth: column.minWidth }}
                  onClick={() => handleHeaderClick(column)}
                >
                  <div className="flex items-center gap-1">
                    {column.header}
                    {getSortIcon(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {safeData.map((item, index) => (
              <tr
                key={item?.id || `row-${index}`}
                className={`
                  hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors
                  ${onRowClick ? 'cursor-pointer' : ''}
                `}
                onClick={() => onRowClick?.(item)}
              >
                {safeColumns.map((column, colIndex) => (
                  <td
                    key={`${item?.id || `row-${index}`}-${column?.key || `col-${colIndex}`}`}
                    className="px-3 lg:px-4 py-3 text-sm text-gray-900 dark:text-gray-100"
                    style={{ minWidth: column?.minWidth }}
                  >
                    {column?.render && item
                      ? column.render(item)
                      : (item as Record<string, unknown>)?.[column?.key] as ReactNode ?? '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      {mobileCardView && renderMobileCards()}
      {renderDesktopTable()}
    </>
  );
}
